import React, { useState, useEffect } from 'react';
import { MessageSquarePlus, ThumbsUp, Loader2 } from 'lucide-react';
import { getSuggestions, addSuggestion, voteSuggestion } from '../services/firebase';
import { Suggestion } from '../types';

const CommunitySuggestions: React.FC = () => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [newSuggestion, setNewSuggestion] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Load suggestions
  const refreshSuggestions = async () => {
    const data = await getSuggestions();
    setSuggestions(data);
    setLoading(false);
  };

  useEffect(() => {
    refreshSuggestions();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSuggestion.trim()) return;

    setSubmitting(true);
    const success = await addSuggestion(newSuggestion);
    if (success) {
      setNewSuggestion('');
      await refreshSuggestions();
    }
    setSubmitting(false);
  };

  const handleVote = async (id?: string) => {
    if (!id) return;
    // Optimistic update
    setSuggestions(prev => prev.map(s => 
      s.id === id ? { ...s, votes: s.votes + 1 } : s
    ));
    await voteSuggestion(id);
  };

  return (
    <div className="w-full max-w-3xl mx-auto px-4 pb-20">
      {/* Input Area */}
      <div className="bg-zinc-900/90 border border-christmas-gold/30 rounded-2xl p-6 mb-8 shadow-lg relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-christmas-green via-christmas-gold to-christmas-red"></div>
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <MessageSquarePlus className="text-christmas-gold" />
          Submit a Christmas Idea
        </h3>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <textarea
            value={newSuggestion}
            onChange={(e) => setNewSuggestion(e.target.value)}
            placeholder="What should be behind the next door?"
            className="w-full bg-black/50 border border-zinc-700 rounded-xl p-4 text-white focus:border-christmas-gold focus:ring-1 focus:ring-christmas-gold outline-none resize-none h-24 transition-all"
            maxLength={200}
          />
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500">{newSuggestion.length}/200</span>
            <button
              type="submit"
              disabled={submitting || !newSuggestion.trim()}
              className="bg-christmas-gold hover:bg-yellow-400 text-black font-bold py-2 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting ? <Loader2 className="animate-spin w-4 h-4" /> : 'Post Idea'}
            </button>
          </div>
        </form>
      </div>

      {/* List Area */}
      <h3 className="text-xl font-festive text-white mb-4 pl-2">Top Suggestions</h3>
      
      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="animate-spin text-christmas-gold w-8 h-8" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {suggestions.map((item) => (
            <div 
              key={item.id} 
              className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 flex gap-4 items-start hover:bg-zinc-900 transition-colors"
            >
              <button 
                onClick={() => handleVote(item.id)}
                className="flex flex-col items-center justify-center bg-zinc-800 hover:bg-zinc-700 min-w-[3.5rem] py-2 rounded-lg transition-colors group"
              >
                <ThumbsUp size={18} className="text-gray-400 group-hover:text-christmas-gold mb-1" />
                <span className="text-sm font-bold text-white">{item.votes}</span>
              </button>
              
              <div className="flex-grow pt-1">
                <p className="text-gray-200 text-sm md:text-base">{item.text}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-zinc-500">by {item.author}</span>
                  <span className="text-zinc-600 text-[10px]">•</span>
                  <span className="text-xs text-zinc-500">Just now</span>
                </div>
              </div>
            </div>
          ))}

          {suggestions.length === 0 && (
             <div className="text-center py-12 text-gray-500">
               No suggestions yet. Be the first!
             </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommunitySuggestions;